import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';

/**
 * Agency-wide settings stored in app_settings (key/value).
 *
 * The agency default commission is what a new campaign starts with when the
 * chosen creator has no default of their own. It used to be a hardcoded 30%
 * repeated across the app; admins now set it once.
 */
export const AGENCY_DEFAULT_COMMISSION_FALLBACK = 0.30;
const COMMISSION_KEY = 'agency_default_commission_pct';

let cached: number | null = null;
let inflight: Promise<number> | null = null;
let unavailable = false;
const listeners = new Set<(pct: number) => void>();

function isValidPct(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
}

/** True when the app_settings table could not be read (pending SQL update). */
export function isSettingsStoreUnavailable(): boolean {
  return unavailable;
}

export async function getAgencyDefaultCommission(): Promise<number> {
  if (cached !== null) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', COMMISSION_KEY)
        .maybeSingle<{ value: unknown }>();
      if (error) throw error;
      const value = Number(data?.value);
      cached = isValidPct(value) ? value : AGENCY_DEFAULT_COMMISSION_FALLBACK;
      unavailable = false;
    } catch (err) {
      // Migrations are applied by hand; until app_settings exists, use the
      // historical default rather than blocking any form.
      log.warn('[settings] app_settings unavailable, using fallback commission:', err);
      cached = AGENCY_DEFAULT_COMMISSION_FALLBACK;
      unavailable = true;
    } finally {
      inflight = null;
    }
    return cached as number;
  })();

  return inflight;
}

export async function setAgencyDefaultCommission(pct: number): Promise<void> {
  if (!isValidPct(pct)) throw new Error('Commission must be between 0 and 1');
  const { data: { user } } = await supabase.auth.getUser();
  // The generated types don't resolve this table's row shape; state it here
  const table = supabase.from('app_settings') as unknown as {
    upsert: (row: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>;
  };
  const { error } = await table.upsert({
    key: COMMISSION_KEY,
    value: pct,
    updated_at: new Date().toISOString(),
    updated_by: user?.id ?? null,
  });
  if (error) throw new Error(error.message);
  cached = pct;
  unavailable = false;
  listeners.forEach((fn) => fn(pct));
}

export function subscribeAgencyDefaultCommission(fn: (pct: number) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
